import { MongoTeamId } from "@redotech/effect-api-shared/ids/mongo-ids";
import type { CustomerEvent } from "@redotech/redo-customer-event-common/customer-event-definition";
import { FulfillmentGroupPickStatus } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import { Order } from "@redotech/redo-order-core-common/order";
import type { MerchantAppReturn } from "@redotech/redo-return-core-common/return";
import {
  getEmailScope,
  type SupportTicketCollectionFilter,
} from "./support-ticket-collection-scope";

export type OrderWithPickStatus = Order & {
  pickStatuses: FulfillmentGroupPickStatus[];
  storeUrl?: string;
  customerId?: string;
};

export interface SupportTicketRelatedRecordsApi {
  getOrders(
    teamId: string,
    identifiers: {
      // Preferred: the server resolves all of the customer's identifiers from it.
      mongoCustomerId?: string;
      // Fallback for tickets with no linked customer.
      emailFallback?: string;
      limit?: number;
    },
  ): Promise<OrderWithPickStatus[]>;
  getReturnsByEmail(
    teamId: string,
    email: string,
    options?: { limit?: number },
  ): Promise<MerchantAppReturn[]>;
  getCustomerEventsByCustomerId(
    teamId: string,
    customerId: string,
    options?: { limit?: number },
  ): Promise<CustomerEvent[]>;
}

/** Read a single `eq` string filter — the only shape these collections push down. */
function getEqFilterValue(
  filters: readonly SupportTicketCollectionFilter[],
  field: string,
): string | null {
  const filter = filters.find(
    (f) => (Array.isArray(f.field) ? f.field[0] : f.field) === field,
  );
  if (filter?.operator !== "eq" || typeof filter.value !== "string") {
    return null;
  }
  return filter.value;
}

export function getTeamIdScope(
  filters: readonly SupportTicketCollectionFilter[],
): string | null {
  const value = getEqFilterValue(filters, "team");
  return value ? MongoTeamId.toRawId(value) : null;
}

/** Customer events filter on the `customer` field (the Redo customer id). */
export function getCustomerIdScope(
  filters: readonly SupportTicketCollectionFilter[],
): string | null {
  return getEqFilterValue(filters, "customer");
}

export function getEmailFilterValue(
  filters: readonly SupportTicketCollectionFilter[],
  fieldName: string,
  validateEmail: boolean,
): string | null {
  return getEmailScope(filters, fieldName, validateEmail);
}

export type OrdersLoadPlan = {
  readonly teamId: string;
  readonly mongoCustomerId?: string;
  readonly emailFallback?: string;
} | null;

/**
 * Extract the identifiers the orders collection loads with: the linked customer
 * id when present (matches orders with no email on the document), else the
 * Shopify email. The server resolves whichever it receives via get-or-create.
 */
export function planOrdersLoad(
  filters: readonly SupportTicketCollectionFilter[],
): OrdersLoadPlan {
  const teamId = getTeamIdScope(filters);
  if (!teamId) {
    return null;
  }
  const mongoCustomerId = getEqFilterValue(filters, "customerId") ?? undefined;
  const emailFallback =
    getEmailFilterValue(filters, "shopify", true) ?? undefined;
  if (!mongoCustomerId && !emailFallback) {
    return null;
  }
  return { teamId, mongoCustomerId, emailFallback };
}
